import { SupabankLogo } from "@/components/brand/SupabankLogo"
import { FooterLocaleSwitcher } from "@/components/footer/FooterLocaleSwitcher"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { SignalDot } from "@/components/ui/signal-dot"
import { Text } from "@/components/ui/typography"
import { sfIcons } from '@/lib/sf-icons'
import { cn } from "@/lib/utils"
import { semanticTokens } from "@supabank/config/theme"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

/**
 * LandingFooter Trust-first footer design
 *
 * Design principles (inspired by Stripe, Wise, Bridge, Mercury):
 * - Typography hierarchy: 13px semibold headers, 14px body
 * - Trust signals as primary content (entity, compliance, security)
 * - Clean multi-column grid with golden ratio
 * - Status indicator for operational transparency
 * - No social icons (not established yet)
 */

const getNavigationColumns = (t: (key: string) => string) => [
  {
    title: t("footer.products"),
    links: [
      { label: t("footer.products.all"), to: "/products" },
      { label: t("footer.products.liquidationAddress"), to: "/products/liquidation-address" },
      { label: t("footer.products.virtualAccount"), to: "/products/virtual-account" },
      { label: t("footer.products.wallet"), to: "/products/wallet" },
      { label: t("footer.products.payoutRoutes"), to: "/products/payout-routes" },
    ],
  },
  {
    title: t("footer.platform"),
    links: [
      { label: t("footer.dashboard"), to: "/dashboard" },
      { label: t("footer.coverageMap"), to: "/coverage" },
      { label: t("footer.pricing"), to: "/fee" },
    ],
  },
  {
    title: t("footer.resources"),
    links: [
      { label: t("footer.supportCenter"), to: "/support" },
      { label: t("footer.joinWaitlist"), to: "/waitlist" },
      { label: t("footer.howPaymentsWork"), to: "/how-payments-work" },
      { label: "Brand Assets", to: "/brand" },
    ],
  },
  {
    title: t("footer.legal"),
    links: [
      { label: t("footer.privacyPolicy"), to: "/privacy" },
      { label: t("footer.termsOfService"), to: "/terms" },
    ],
  },
]

const getSecurityPillars = (t: (key: string) => string) => [
  { icon: sfIcons.security, label: t("footer.zeroTrustArchitecture") },
  { icon: sfIcons.lock, label: t("footer.endToEndEncryption") },
  { icon: sfIcons.checkCircle2, label: t("footer.rowLevelSecurity") },
]

export function LandingFooter() {
  const { t } = useTranslation("common")
  const navigationColumns = getNavigationColumns(t)
  const securityPillars = getSecurityPillars(t)

  // Reliable dark theme detection
  const [isDark, setIsDark] = useState(false)
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(document.documentElement.classList.contains('dark'))
    }
    // Initial check
    checkDarkMode()
    // Watch for class changes
    const observer = new MutationObserver(checkDarkMode)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    })
    return () => observer.disconnect()
  }, [])
  return (
    <footer
      className={cn(
        "relative pt-20 pb-8 overflow-hidden",
        "bg-slate-50 dark:bg-slate-950",
        semanticTokens.harmony.calm.border,
        "border-t"
      )}
    >
      {/* Color Lab: Premium dark gradient (slate-900 → slate-950 style) */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse at 50% 0%, hsla(220, 15%, 20%, 0.08) 0%, transparent 50%),
            radial-gradient(ellipse at 0% 100%, hsla(270, 95%, 75%, 0.04) 0%, transparent 40%),
            radial-gradient(ellipse at 100% 100%, hsla(217, 91%, 60%, 0.04) 0%, transparent 40%)
          `
        }}
      />
      <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6">
        {/* Main Grid: 5-column golden ratio */}
        <div className="grid gap-12 lg:grid-cols-5">

          {/* Left: Brand + Trust (3/5 = 60%) */}
          <div className="lg:col-span-3 space-y-8">
            {/* Brand Mark */}
            <div className="space-y-4">
              <SupabankLogo variant="full" size="md" inverted={isDark} />
              <Text variant="sm" color="muted" className="max-w-md leading-relaxed">
                {t("footer.description")}
              </Text>
            </div>

            {/* Security Pillars Premium Glass with Brand Identity */}
            <div className="flex flex-wrap gap-3">
              {securityPillars.map((pillar, index) => {
                const Icon = pillar.icon || sfIcons.security
                if (!Icon) return null
                return (
                  <div
                    key={pillar.label}
                    className={cn(
                      "group relative inline-flex items-center gap-2 px-4 py-2 rounded-full",
                      // Light theme: Premium background using positive.bg with enhanced contrast
                      semanticTokens.positive.bg,
                      "bg-gradient-to-br from-green-50 via-white to-green-50/60",
                      "backdrop-blur-lg backdrop-saturate-[180%]",
                      // Light theme: Canonical positive border with enhanced visibility
                      semanticTokens.positive.border,
                      "border-green-200",
                      // Light theme: Multi-layer shadows for premium depth (canonical approach)
                      "shadow-[0_4px_16px_hsl(142_71%_45%/0.10),0_1px_4px_hsl(220_15%_5%/0.08),inset_0_1px_0_hsl(0_0%_100%/0.9)]",
                      // Dark theme: Premium Glass Elevated tier with enhanced visibility
                      "dark:bg-gradient-to-br dark:from-slate-800/85 dark:via-slate-900/75 dark:to-slate-800/85",
                      "dark:backdrop-blur-xl dark:backdrop-saturate-[160%] dark:brightness-95",
                      "dark:border dark:border-white/15",
                      "dark:shadow-[0_12px_40px_hsl(0_0%_0%/0.6),0_4px_16px_hsl(142_71%_45%/0.15),inset_0_1px_0_hsl(0_0%_100%/0.12)]",
                      // Canonical motion (240ms)
                      semanticTokens.motion.standard,
                      // Hover states with enhanced brand glow (light theme)
                      "hover:shadow-[0_8px_28px_hsl(142_71%_45%/0.18),0_2px_8px_hsl(220_15%_5%/0.10),inset_0_1px_0_hsl(0_0%_100%/1)]",
                      "hover:border-green-300",
                      "dark:hover:shadow-[0_0_24px_hsl(142_71%_45%/0.4),0_8px_32px_hsl(0_0%_0%/0.7),inset_0_1px_0_hsl(0_0%_100%/0.15)]",
                      "dark:hover:border-green-400/30",
                      "hover:-translate-y-0.5",
                      "hover:scale-[1.02]",
                      // Stagger animation delay for entrance
                      "animate-in fade-in slide-in-from-bottom-2",
                      "duration-240 ease-standard"
                    )}
                    style={{
                      animationDelay: `${index * 50}ms`
                    }}
                  >
                    {/* Light theme: Enhanced glow on hover with brand colors */}
                    <div className={cn(
                      "absolute inset-0 rounded-full opacity-0",
                      "group-hover:opacity-100",
                      "bg-gradient-to-r from-green-100/50 via-emerald-50/30 to-green-100/50",
                      "dark:from-green-400/30 dark:via-emerald-400/20 dark:to-green-400/30",
                      semanticTokens.motion.standard,
                      "pointer-events-none"
                    )} />

                    {/* Light theme: Specular highlight for premium feel (canonical glass effect) */}
                    <div className={cn(
                      "absolute inset-x-0 top-0 h-px rounded-full",
                      semanticTokens.glass.specular,
                      // Dark theme: Enhanced specular highlight for better visibility
                      "dark:bg-gradient-to-r dark:from-transparent dark:via-white/40 dark:to-transparent",
                      "dark:shadow-[0_0_8px_hsl(0_0%_100%/0.3)]",
                      "pointer-events-none"
                    )} />

                    {/* Icon with subtle pulse animation */}
                    <Icon
                      className={cn(
                        "h-3.5 w-3.5 relative z-10",
                        // Light theme: Canonical positive.accent for visibility
                        semanticTokens.positive.accent,
                        "text-green-600",
                        // Dark theme: Brighter glowing icons for premium feel
                        "dark:text-green-400",
                        "dark:drop-shadow-[0_0_4px_hsl(142_71%_45%/0.6)]",
                        "group-hover:text-green-700",
                        "dark:group-hover:text-green-300",
                        "dark:group-hover:drop-shadow-[0_0_8px_hsl(142_71%_45%/0.8)]",
                        "group-hover:scale-110",
                        semanticTokens.motion.standard,
                        // Subtle pulse animation (respects prefers-reduced-motion)
                        "animate-pulse"
                      )}
                      aria-hidden="true"
                      style={{
                        animationDuration: '2s',
                        animationIterationCount: 'infinite'
                      }}
                    />

                    {/* Text with brand color on hover */}
                    <Text
                      variant="caption-sm"
                      color="muted"
                      as="span"
                      className={cn(
                        "relative z-10 font-medium",
                        // Light theme: Canonical positive.fg for better contrast
                        semanticTokens.positive.fg,
                        "text-green-700",
                        // Dark theme: Brighter text for better contrast
                        "dark:text-slate-200",
                        "group-hover:text-green-800",
                        "dark:group-hover:text-white",
                        semanticTokens.motion.standard
                      )}
                    >
                      {pillar.label}
                    </Text>
                  </div>
                )
              })}
            </div>

            {/* Operational Status Living Brand: subtle mint pulse */}
            <div className="flex items-center gap-2 pt-2">
              <SignalDot signal="static" size="xs" color="mint" />
              <Text variant="caption" color="muted" className="font-medium">
                {t("footer.allSystemsOperational")}
              </Text>
            </div>
          </div>

          {/* Right: Navigation (2/5 = 40%) */}
          <div className="lg:col-span-2">
            {/* Mobile Accordion */}
            <div className="md:hidden">
              <Accordion type="multiple" className="divide-y">
                {navigationColumns.map((column) => (
                  <AccordionItem key={column.title} value={column.title}>
                    <AccordionTrigger
                      className={cn(
                        semanticTokens.typography.bodySmall,
                        "font-semibold",
                        semanticTokens.fg.default,
                        semanticTokens.focus.ring
                      )}
                    >
                      {column.title}
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-2.5 pb-2">
                        {column.links.map((item) => (
                          <li key={item.label}>
                            {item.external ? (
                              <a
                                href={item.to}
                                target="_blank"
                                rel="noreferrer"
                                className={cn(
                                  "inline-flex items-center gap-1",
                                  semanticTokens.typography.bodySmall,
                                  semanticTokens.fg.muted,
                                  "hover:text-foreground transition-colors"
                                )}
                              >
                                {item.label}
                                <sfIcons.externalLink className="h-3 w-3" />
                              </a>
                            ) : (
                              <Link
                                to={item.to}
                                className={cn(
                                  semanticTokens.typography.bodySmall,
                                  semanticTokens.fg.muted,
                                  "hover:text-foreground transition-colors"
                                )}
                              >
                                {item.label}
                              </Link>
                            )}
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>

            {/* Desktop Grid */}
            <div className="hidden md:grid gap-8 md:grid-cols-4">
              {navigationColumns.map((column) => (
                <nav key={column.title} className="space-y-4" aria-label={column.title}>
                  <Text variant="sm" color="default" className="font-semibold">
                    {column.title}
                  </Text>
                  <ul className="space-y-2.5">
                    {column.links.map((item) => (
                      <li key={item.label}>
                        {item.external ? (
                          <a
                            href={item.to}
                            target="_blank"
                            rel="noreferrer"
                            className={cn(
                              "inline-flex items-center gap-1",
                              semanticTokens.focus.ring,
                              "rounded-sm"
                            )}
                          >
                            <Text variant="caption" color="muted" as="span" className="hover:text-foreground transition-colors">
                              {item.label}
                            </Text>
                            <sfIcons.externalLink className={cn("h-3 w-3", semanticTokens.fg.subtle)} />
                          </a>
                        ) : (
                          <Link
                            to={item.to}
                            className={cn(
                              semanticTokens.focus.ring,
                              "rounded-sm"
                            )}
                          >
                            <Text variant="caption" color="muted" as="span" className="hover:text-foreground transition-colors">
                              {item.label}
                            </Text>
                          </Link>
                        )}
                      </li>
                    ))}
                  </ul>
                </nav>
              ))}
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className={cn("my-10 border-t", semanticTokens.border.subtle)} />

        {/* Bottom: Entity + Compliance */}
        <div className="grid gap-8 lg:grid-cols-5">

          {/* Entity Information (3/5) */}
          <div className="lg:col-span-3 space-y-6">
            <div className="space-y-2">
              <Text as="p" variant="caption" color="default" className="font-semibold">
                {t("footer.entityName")}
              </Text>
              <Text as="p" variant="caption-sm" color="muted">
                {t("footer.entityAddress")}
              </Text>
            </div>

            <div className="space-y-3">
              <Text variant="caption-sm" color="subtle" className="leading-relaxed max-w-lg">
                {t("footer.entityDescription")}
              </Text>
            </div>
          </div>

          {/* Settlement Partner (2/5) */}
          <div className="lg:col-span-2 space-y-4">
            <div className="space-y-2">
              <Text as="p" variant="caption" color="default" className="font-semibold">
                {t("footer.settlementPartner")}
              </Text>
              <Text as="p" variant="caption-sm" color="muted" className="leading-relaxed">
                {t("footer.settlementPartnerDescription")}
              </Text>
            </div>
            <a
              href="https://bridge.xyz/legal"
              target="_blank"
              rel="noreferrer"
              className={cn(
                "inline-flex items-center gap-1.5",
                semanticTokens.info.accent,
                "hover:underline",
                semanticTokens.focus.ring,
                "rounded-sm"
              )}
            >
              <Text variant="caption-sm" as="span">{t("footer.viewLicensesCompliance")}</Text>
              <sfIcons.externalLink className="h-3 w-3" />
            </a>
          </div>
        </div>

        {/* Final Divider */}
        <div className={cn("my-8 border-t", semanticTokens.border.subtle)} />

        {/* Legal Disclaimer - WCAG AA compliant (≥13px, ≥4.5:1 contrast) */}
        <div className="rounded-lg bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 p-4 mb-6">
          <Text variant="sm" className="text-slate-600 dark:text-slate-400 leading-relaxed max-w-3xl">
            <span className="font-semibold text-slate-700 dark:text-slate-300">Important: </span>
            {t("footer.disclaimer")}
          </Text>
        </div>

        {/* Copyright */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Text variant="caption" color="muted">
            {t("footer.copyright", { year: new Date().getFullYear() })}
          </Text>
          <FooterLocaleSwitcher />
        </div>
      </div>
    </footer>
  )
}
